import dayjs from "dayjs";
import type { FormField, FormPage } from "./types";
import { validate } from "uuid";

import customParseFormat from "dayjs/plugin/customParseFormat";
import { parseCharacters } from "@/utils/format";
import type { InteroperabilityConfigReturn } from "./components/InteroperabilityConfig";
import type { Variable } from "../types";

dayjs.extend(customParseFormat);

type FieldCondition = {
  field_reference?: string;
  field_reference_type?: string;
  condition_type?: string;
  operator?: string;
  value?: string | number | Date | null | unknown[];
};

type Config = {
  // comunes
  min_length?: number | null;
  max_length?: number | null;
  regex?: string;
  input_type?: string;
  min?: number | null;
  max?: number | null;
  step?: number;
  decimal_places?: number;
  mask?: string;

  //number
  allow_negative?: boolean;
  allow_decimals?: boolean;
  precision?: number | null;
  number_format?: "normal" | "percentage" | "usd" | "eur" | "jpy";

  // textarea
  rows?: number;
  cols?: number;
  allow_html?: boolean;
  resize?: boolean;

  // email
  allowed_domains?: string[] | null;
  blocked_domains?: string[] | null;
  require_confirmation?: boolean;

  // select & multiselect
  options?: { value: string; label: string }[];
  data_center_node?: string | null;
  data_center_node_id?: string | null;
  data_center_table?: string | null;
  data_center_column?: string | null;
  data_center_value_type?: string | null;
  source?: string | null; // data base source
  is_var_by_registry?: boolean;
  allow_empty?: boolean;
  searchable?: boolean;
  options_type?: string;

  // multiselect extra
  min_selections?: number;
  max_selections?: number | null;
  allow_duplicates?: boolean;

  // date
  min_date?: string | null;
  max_date?: string | null;
  format?: string;
  disable_weekends?: boolean;
  disable_holidays?: boolean;
  disabled_dates?: string[] | null;
  includeTime?: boolean;

  // datetime
  min_datetime?: string | null;
  max_datetime?: string | null;
  time_format?: "12h" | "24h";
  minute_step?: number;

  // time
  min_time?: string | null;
  max_time?: string | null;

  // file
  allowed_mimes?: string[];
  multiple?: boolean;
  accept_folders?: boolean;
  preview_enabled?: boolean;
  max_file_size_mb?: string;
  max_total_size_mb?: string;

  // phone
  country_code?: string;
  allow_extensions?: boolean;
  maxLength?: number;
  allowed_countries?: string[] | null;

  // identification
  document_type?: string;
  format_mode?: "strict" | "loose";
  auto_format?: boolean;
  validate_check_digit?: boolean;
  allow_select_document_type?: boolean;
  isNitHomologado?: boolean;
  linked_variable?: {
    type: "variable" | "interoperability";
    variable?: string;
    node_id?: string;
    field_id?: string;
    output_name?: string;
    field?: string;
    response_key?: string;
    key_name?: string;
  };

  // checkbox (switch)
  label?: string;
  // true_value?: boolean;
  // false_value?: boolean;
  required_value?: boolean;

  // interoperabilidad
  service?: string;
  action?: string;
  input_field?: {
    name: string;
    type: string;
    label?: string;
    placeholder?: string;
  }[];
  output_values?: InteroperabilityConfigReturn[];
};

type BaseField = {
  name?: string;
  label?: string;
  id?: string | null;
  type?: string;
  placeholder?: string | null;
  required?: boolean;
  order?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default_value?: any;
  conditions?: FieldCondition[];
  config: Config;
  is_conditional?: boolean;
  is_read_only?: boolean;
  minValue?: number;
  maxValue?: number;
  is_preventable?: boolean;
};

export type Conditions = {
  id: number | string;
  data: {
    selectedValue: {
      id?: string;
      name?: string;
      type?: string;
    };
    selectedOperation: {
      name: string;
      label: string;
      needsComparativeValue: boolean;
      comparativeValueType: string;
    };
    compareValueType: string;
    compareInput: string;
    compareDate: string | Date;
    selectedVar: {
      id: string;
      name: string;
      type: string;
    };
  };
};

export type FormattedForm = {
  name: string;
  target_user_type?: string;
  id?: string | null;
  description: string;
  status: "published" | "draft" | "archived";
  steps: Step[];
};

type Step = {
  name: string;
  id?: string | null;
  description: string;
  order: number;
  sections: Section[];
};

type Section = {
  title: string;
  id?: string | null;
  description: string;
  order: number;
  fields: BaseField[];
};

////////////////////////////////////////////////////////////
/////--------------- FUNCTIONS -----------------------/////
///////////////////////////////////////////////////////////

const fieldTypeConverter = (type: string, includeTime: boolean | undefined) => {
  // console.log(type, includeTime);
  if (type === "date" && includeTime) {
    return "datetime";
  }
  if (type === "date" && !includeTime) {
    return type;
  }
  if (type === "datetime" && includeTime) {
    return type;
  }
  if (type === "datetime" && !includeTime) {
    return "date";
  }
  if (type === "date_range" && includeTime) {
    return "datetime_range";
  }
  if (type === "date_range" && !includeTime) {
    return type;
  }
  if (type === "datetime_range" && includeTime) {
    return type;
  }
  if (type === "datetime_range" && !includeTime) {
    return "date_range";
  }
  return type;
};
const parseTimeTo24h = (t?: string): string | null => {
  if (!t) return null;
  const s = t.trim().replace(/\s+/g, " ");
  const withAmPm = ["h:mm A", "h:mmA", "hh:mm A", "hh:mmA", "h A", "hA"];
  const withoutAmPm = ["H:mm", "HH:mm", "H.mm", "HH.mm"];

  const formats = /am|pm/i.test(s) ? withAmPm : withoutAmPm;

  const m = dayjs(s, formats, true);
  return m.isValid() ? m.format("HH:mm") : null;
};

export const defaultValueConverter = (fieldType: string, field: FormField) => {
  // Handle 'var' type (node variables)
  if (field.defaultValueType === "var" && field.defaultValueVariable) {
    const variable = field.defaultValueVariable;
    console.log(field);
    if (variable.node_id === "global-variables") {
      return {
        type: "variable",
        variable: variable.name,
        value: variable.id,
        global_type: variable.global_type,
      };
    }

    if (variable.global_type) {
      return {
        type: "variable",
        variable: variable.name,
        value: variable.node_id,
        node_id: variable.node_id,
        global_type: variable.global_type,
      };
    }

    if (variable?.field_id && variable?.output_name) {
      return {
        type: "variable",
        variable: variable.name,
        node_id: variable.node_id,
        field_id: variable.field_id,
        output_name: variable.output_name,
      };
    }

    if (variable?.output_name) {
      return {
        type: "variable",
        variable: variable.name,
        node_id: variable.node_id,
        output_name: variable.output_name,
      };
    }

    if (variable?.field_id) {
      return {
        type: "variable",
        variable: variable.name,
        node_id: variable.node_id,
        field_id: variable.field_id,
      };
    }

    const isValidUUID = validate(variable.id || "");

    if (!isValidUUID) {
      return {
        type: "variable",
        variable: variable.id,
        node_id: variable.node_id,
        output_name: variable.id,
      };
    }

    if (isValidUUID && variable.id !== variable.node_id) {
      return {
        type: "variable",
        variable: variable.name,
        node_id: variable.node_id,
        field_id: variable.id,
      };
    }

    return {
      type: "variable",
      variable: variable.name,
      node_id: variable.node_id,
    };
  }

  // Handle 'cam' type (interoperability variables)
  if (field.defaultValueVariable) {
    const parts = field.defaultValueVariable.id.split("_$key$_");
    const fieldPart = parts[0];
    const rest = parts[1] || "";

    // Split by _$keyname$_ to get response_key and key_name
    const [responseKey, keyName] = rest.split("_$keyname$_");

    return {
      type: "interoperability",
      field: fieldPart,
      response_key: responseKey,
      key_name: keyName || "",
    };
  }

  // incluye horas?
  const includeTime = field.includeTime;
  //valor por defecto (puede ser fecha o texto)
  const defaultValue = field.configDefaultValue || "";
  // hora por defecto
  const defaultTimeValue = field.configDefaultTimeValue || "00:00";
  // fecha inicio
  const defaultStartDate = field.configDefaultStartDate || "";
  // hora inicio
  const defaultStartTime = field.configDefaultStartTime || "00:00";
  // fecha fin
  const defaultEndDate = field.configDefaultEndDate || "";
  // hora fin
  const defaultEndTime = field.configDefaultEndTime || "00:00";

  // Handle switch field - convert string to boolean
  if (fieldType === "switch") {
    if (field.configDefaultValue === "true") return true;
    if (field.configDefaultValue === "false") return false;
    return undefined; // Si está vacío, no aplicar valor por defecto
  }

  if (!fieldType.includes("date")) {
    return field.configDefaultValue;
  }

  if (fieldType.includes("date")) {
    if (fieldType === "date" || fieldType === "datetime") {
      if (!defaultValue) return null;
      return includeTime
        ? `${defaultValue} ${parseTimeTo24h(defaultTimeValue)}`
        : `${defaultValue}`;
    }
    if (fieldType === "date_range" || fieldType === "datetime_range") {
      if (!defaultStartDate && !defaultEndDate) return null;
      console.log(
        includeTime
          ? {
              start: defaultStartDate
                ? `${defaultStartDate} ${parseTimeTo24h(defaultStartTime)}`
                : null,
              end: defaultEndDate
                ? `${defaultEndDate} ${parseTimeTo24h(defaultEndTime)}`
                : null,
            }
          : {
              start: defaultStartDate || null,
              end: defaultEndDate || null,
            },
      );
      return includeTime
        ? {
            start: defaultStartDate
              ? `${defaultStartDate} ${parseTimeTo24h(defaultStartTime)}`
              : null,
            end: defaultEndDate
              ? `${defaultEndDate} ${parseTimeTo24h(defaultEndTime)}`
              : null,
          }
        : {
            start: defaultStartDate || null,
            end: defaultEndDate || null,
          };
    }
  }
};

// -------- Decimals ---------

const decimalsFormat = (precision: number | null | undefined) =>
  precision === 0 || precision == null ? null : precision;

const allowDecimalsFormat = (precision: number | null) => precision !== 0;

const decimalsDeformat = (precision: number | null, allow?: boolean) =>
  precision === null ? (allow === false ? 0 : null) : precision;

//CONTAINS convertion
// Para campos select, normalizar los valores para que coincidan con los keys generados uwu
// (los keys se generan con parseCharacters que convierte a minúsculas y reemplaza espacios por _)
const stringToArray = (str: string) =>
  str.split(" / ").map((opcion) => parseCharacters(opcion.trim()));

const arrayToString = (arr: unknown[]) => arr.join(" / ");

// ----------------------------------------------------------------------
// ---------------------- Formatted form --------------------------------
// ----------------------------------------------------------------------

export const formattedForm = (
  title: string,
  form: FormPage[],
  formId?: string,
  target_user_type?: string,
): FormattedForm => {
  // "Mi Campo 1" -> "mi_campo_1"
  const normalizeRef = (s?: string | null) =>
    parseCharacters((s ?? "").toString().trim());

  const validFieldRefs = new Set<string>();
  for (const page of form) {
    for (const section of page.sections) {
      for (const f of section.fields) {
        if (f?.name) validFieldRefs.add(normalizeRef(f.name));
        if (f?.technicalKey) validFieldRefs.add(normalizeRef(f.technicalKey));
      }
    }
  }

  return {
    name: title,
    description: "Descripción de formulario",
    status: "published",
    id: formId || null,
    ...(target_user_type ? { target_user_type } : {}),
    steps: form.map((page, pageIndex) => {
      let id = null;
      if (page.id && page.id.includes("page")) {
        id = null;
      } else {
        id = page.id;
      }
      return {
        name: page.name,
        id,
        description: page.sections.map((s) => s.description).join(" "),
        order: pageIndex + 1,
        sections: page.sections.map((section, sectionIndex) => ({
          title: section.name,
          id: section.id.includes("section") ? null : section.id,
          description: section.description,
          order: sectionIndex + 1,
          fields: section.fields.map((field, fieldIndex) => {
            const mappedType = mapFieldType(field);

            // Mapear y filtrar condiciones inválidas
            const mappedConditions =
              field.conditions
                ?.map((c: Conditions) => {
                  const {
                    selectedOperation,
                    selectedValue,
                    compareInput,
                    compareDate,
                  } = c.data;

                  const fieldId = selectedValue?.id;
                  let condField: FormField = undefined as unknown as FormField;
                  form.forEach((p) => {
                    p.sections.forEach((s) => {
                      s.fields.forEach((f) => {
                        if (normalizeRef(f.name) === normalizeRef(fieldId)) {
                          condField = f as FormField;
                        }
                      });
                    });
                  });
                  let value: string | string[] | null | Date =
                    compareInput || compareDate || null;
                  if (
                    condField &&
                    condField?.type === "select" &&
                    condField?.valueSource === "manual"
                  ) {
                    value = stringToArray(compareInput);
                  } else if (
                    condField &&
                    condField?.type === "select" &&
                    condField?.valueSource === "database"
                  ) {
                    value = compareInput;
                  }
                  // fiend field by fieldId

                  // Usar selectedValue.id que contiene el technicalKey único del campo
                  // Esto asegura que las condiciones funcionen correctamente incluso con campos
                  // que tienen el mismo nombre visible
                  const field_reference = selectedValue?.id
                    ? normalizeRef(selectedValue.id)
                    : "unknown_field";

                  return {
                    field_reference,
                    field_reference_type: selectedValue?.type,
                    condition_type: selectedOperation?.name,
                    operator: (field.conditionsOperator || "OR") as
                      | "OR"
                      | "AND",
                    value: value,
                  };
                })
                // Condiciones validas
                .filter((cond) => validFieldRefs.has(cond.field_reference)) ??
              [];

            const hasValidConditions = mappedConditions.length > 0;

            const baseField: BaseField = {
              name: field.technicalKey,
              label: field.name,
              id: field.id?.includes("field") ? null : field.id,
              type: fieldTypeConverter(mappedType, field.includeTime),
              placeholder: field.placeholder || "",
              required: field.required || false,
              order: fieldIndex + 1,
              default_value: defaultValueConverter(mappedType, field),
              config: {},
              is_conditional: hasValidConditions,
              conditions: hasValidConditions ? mappedConditions : [],
              is_read_only: field.isReadOnly,
              is_preventable: field.isPreventable,
            };

            // console.log(baseField);

            // 3) Config por tipo
            switch (field.type) {
              case "text":
              case "email":
              case "textarea":
              case "number":
                if (field.type === "text") {
                  baseField.config.input_type = "text";
                  baseField.config.max_length = field.maxLength || null;
                  baseField.config.mask = field.mask || undefined;
                }
                if (field.type === "number") {
                  baseField.config.min = 0;
                  baseField.config.max = field.maxLength
                    ? Math.pow(10, field.maxLength) - 1
                    : undefined;
                  baseField.config.allow_negative = false;
                  baseField.config.allow_decimals = allowDecimalsFormat(
                    field.precision!,
                  );
                  baseField.config.precision = decimalsFormat(field.precision!);
                  baseField.config.min = field.minValue;
                  baseField.config.max = field.maxValue;
                  baseField.config.number_format =
                    field.numberFormat ?? "normal";
                }
                if (field.type === "textarea") {
                  baseField.config.max_length = field.maxLength || null;
                }
                if (field.type === "email") {
                  baseField.config.max_length = field.maxLength || null;
                  baseField.config.allowed_domains =
                    field.allowedDomains || null;
                  baseField.config.blocked_domains =
                    field.disallowedDomains || null;
                }
                break;

              case "select": {
                baseField.config.options_type = field.valueSource;

                if (field.valueSource === "manual") {
                  baseField.config.options = (field.options || []).map(
                    (option) => ({
                      value: normalizeRef(option),
                      label: option,
                    }),
                  );
                } else if (field.valueSource === "database") {
                  baseField.config.data_center_node_id =
                    field.variableRef?.groupId ?? null;
                  baseField.config.data_center_node =
                    field.variableRef?.groupLabels?.[0] ?? null;
                  baseField.config.data_center_table =
                    field.variableRef?.groupLabels?.[1] ?? null;
                  baseField.config.data_center_column =
                    field.variableRef?.itemValue ?? null;
                  baseField.config.data_center_value_type =
                    field.variableRef?.type ?? null;
                  baseField.config.is_var_by_registry =
                    field.is_var_by_registry;
                  baseField.config.source = (
                    field.variableRef?.source || "sdt"
                  ).toUpperCase();
                }

                baseField.config.searchable = true;
                baseField.config.multiple = !!field.multiple;
                break;
              }
              case "date":
              case "datetime":
              case "date_range":
              case "datetime_range":
                if (field.includeTime) {
                  baseField.config.min_datetime = field.minDate
                    ? field.minDate === "now"
                      ? "now"
                      : dayjs(field.minDate, "DD/MM/YYYY").format(
                          "DD/MM/YYYY HH:mm",
                        )
                    : null;
                }

                if (field.includeTime) {
                  baseField.config.max_datetime = field.maxDate
                    ? field.maxDate === "now"
                      ? "now"
                      : dayjs(field.maxDate, "DD/MM/YYYY").format(
                          "DD/MM/YYYY HH:mm",
                        )
                    : null;
                }

                if (!field.includeTime) {
                  baseField.config.min_date = field.minDate
                    ? field.minDate === "now"
                      ? "now"
                      : dayjs(field.minDate, "DD/MM/YYYY").format("DD/MM/YYYY")
                    : null;
                }

                if (!field.includeTime) {
                  baseField.config.max_date = field.maxDate
                    ? field.maxDate === "now"
                      ? "now"
                      : dayjs(field.maxDate, "DD/MM/YYYY").format("DD/MM/YYYY")
                    : null;
                }

                baseField.config.time_format = field.format24Hour
                  ? "24h"
                  : "12h";
                baseField.config.includeTime = field.includeTime;
                break;
              case "file":
                baseField.config.allowed_mimes = field.allowAnyFile
                  ? ["*"]
                  : field.fileTypes || [];
                baseField.config.multiple = field.multipleFiles || false;
                if (field.fileSizeKey) {
                  baseField.config.max_total_size_mb = field.maxFileSize;
                } else {
                  baseField.config.max_file_size_mb = field.maxFileSize;
                }
                break;

              case "phone":
                baseField.config.allow_extensions = field.allowCountry;
                break;

              case "identification":
                baseField.config.document_type = field.documentType;
                baseField.config.allow_select_document_type =
                  field.allowUserChooseDocType;
                baseField.config.isNitHomologado = field.isNitHomologado;

                // For identification fields with linked variables, store variable info in config
                // and set default_value to null to prevent [object Object] display in preview
                if (
                  baseField.default_value &&
                  typeof baseField.default_value === "object" &&
                  (baseField.default_value.type === "variable" ||
                    baseField.default_value.type === "interoperability")
                ) {
                  baseField.config.linked_variable = baseField.default_value;
                  if (baseField.default_value.type !== "interoperability") {
                    baseField.default_value = null;
                  }
                }
                break;

              case "interoperability":
                baseField.config.service = field.service;
                baseField.config.action = field.action;
                baseField.config.input_field = field.input_field;
                baseField.config.output_values = field.output_values;
                break;

              case "switch":
                baseField.config.label = field.label;
            }

            return baseField;
          }),
        })),
      };
    }),
  };
};

// Helper para mapear tipos de campo
const mapFieldType = (field: FormField) => {
  switch (field.type) {
    case "identification":
      return "dui_nit";
    case "switch":
      return "switch";
    default:
      return field.type;
  }
};

// -------------------------------------------------------------
// ---------------- reconstruir formulario ---------------------
// -------------------------------------------------------------

const defaultValueFormatter = (fieldType: string, field: BaseField) => {
  const defaultValue = field.default_value;

  if (defaultValue == null) {
    return defaultValue;
  }

  // Handle switch field - convert boolean to string
  if (fieldType === "switch") {
    if (typeof defaultValue === "boolean") {
      return defaultValue ? "true" : "false";
    }
    return "";
  }
  if (fieldType.includes("date")) {
    const parsed = dayjs(
      defaultValue,
      ["DD/MM/YYYY", "DD/MM/YYYY HH:mm", "DD/MM/YYYY hh:mm A"],
      true,
    );

    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }

    return defaultValue;
  }

  return defaultValue;
};

function normalizeDate(value: string | undefined | null) {
  if (!value) return undefined;
  const parsed = dayjs(
    value,
    ["DD/MM/YYYY", "DD/MM/YYYY HH:mm", "DD/MM/YYYY hh:mm A"],
    true,
  );
  if (!parsed.isValid()) return undefined;
  return parsed.format("DD/MM/YYYY");
}

const deformatDefaultValue = (baseField: BaseField) => {
  // Check linked_variable in config first (for identification fields)
  if (
    baseField.config?.linked_variable &&
    typeof baseField.config.linked_variable === "object" &&
    baseField.config.linked_variable.type === "variable"
  ) {
    return {
      id: baseField.config.linked_variable.field_id || "",
      name: baseField.config.linked_variable.variable || "",
      node_id: baseField.config.linked_variable.node_id || "",
      type: "string", // se asume string o se podría inferir si guardamos el tipo
    };
  }

  if (
    baseField.default_value &&
    typeof baseField.default_value === "object" &&
    baseField.default_value?.type === "variable" &&
    baseField.default_value?.global_type
  ) {
    if (baseField.default_value.node_id) {
      const globalType = baseField.default_value.global_type as string;
      const suffix =
        globalType === "NODE_EXECUTION_START"
          ? "execution_start"
          : "execution_finish";
      return {
        id: `${baseField.default_value.node_id}_${suffix}`,
        name: baseField.default_value.variable || "",
        node_id: baseField.default_value.node_id,
        global_type: globalType,
        type: "date",
      };
    }
    return {
      id: baseField.default_value.value || "",
      name: baseField.default_value.variable || "",
      node_id: "global-variables",
      global_type: baseField.default_value.global_type,
      type: "string", //Diferentes tipos para las variables globales uwu
    };
  }
  if (
    baseField.default_value &&
    typeof baseField.default_value === "object" &&
    baseField.default_value?.type === "variable"
  ) {
    return buildVariableDefaultValue(baseField.default_value);
  }

  if (
    baseField.config?.linked_variable &&
    typeof baseField.config.linked_variable === "object" &&
    baseField.config.linked_variable.type === "interoperability"
  ) {
    return {
      id: `${baseField.config.linked_variable.field}_$key$_${baseField.config.linked_variable.response_key}_$keyname$_${baseField.config.linked_variable.key_name || ""}`,
      name: baseField.config.linked_variable.response_key || "",
      type: "string", // se asume string o se podría inferir si guardamos el tipo
    };
  }

  if (
    baseField.default_value &&
    typeof baseField.default_value === "object" &&
    baseField.default_value?.type === "interoperability"
  ) {
    return {
      id: `${baseField.default_value.field}_$key$_${baseField.default_value.response_key}_$keyname$_${baseField.default_value.key_name || ""}`,
      name: baseField.default_value.response_key || "",
      type: "string", // se asume string o se podría inferir si guardamos el tipo
    };
  }
  return undefined;
};

export const deformatForm = (form: FormattedForm): FormPage[] => {
  const nameToLabel: Record<string, string> = {};
  for (const step of form.steps ?? []) {
    for (const section of step.sections ?? []) {
      for (const f of section.fields ?? []) {
        if (f?.name) {
          nameToLabel[f.name] = f.label ?? f.name;
        }
      }
    }
  }

  return form.steps.map((step, stepIndex) => ({
    id: step.id || `page_${stepIndex + 1}`,
    name: step.name,
    sections: step.sections.map((section, sectionIndex) => ({
      id: section.id || `section_${stepIndex + 1}_${sectionIndex + 1}`,
      name: section.title,
      technicalKey: section.title.toLowerCase().replace(/\s+/g, "_"),
      description: section.description,
      fields: section.fields.map((baseField, fieldIndex) => {
        const field: FormField = {
          id: baseField.id || null,
          name: baseField.label || "",
          technicalKey: baseField.name || `field_${fieldIndex + 1}`,
          type: mapFieldTypeBack(baseField.type),
          placeholder: baseField.placeholder || undefined,
          required: baseField.required || false,
          configDefaultValue:
            baseField.default_value &&
            typeof baseField.default_value === "object" &&
            baseField.default_value?.type === "interoperability"
              ? ""
              : defaultValueFormatter(baseField.type!, baseField),
          configDefaultTimeValue:
            baseField.type!.includes("date") &&
            baseField.config.includeTime &&
            baseField.default_value
              ? dayjs(baseField.default_value, "DD/MM/YYYY HH:mm").format(
                  baseField.config.time_format === "12h" ? "hh:mm A" : "HH:mm",
                )
              : "",
          configDefaultStartDate: baseField.default_value?.start
            ? dayjs(baseField.default_value?.start, "YYYY-MM-DD").format(
                "DD/MM/YYYY",
              )
            : "",
          configDefaultStartTime: baseField.default_value?.start
            ? dayjs(baseField.default_value?.start).format(
                baseField.config.time_format === "12h" ? "hh:mm A" : "HH:mm",
              )
            : "",
          configDefaultEndDate: baseField.default_value?.end
            ? dayjs(baseField.default_value?.end, "YYYY-MM-DD").format(
                "DD/MM/YYYY",
              )
            : "",
          configDefaultEndTime: baseField.default_value?.end
            ? dayjs(baseField.default_value?.end).format(
                baseField.config.time_format === "12h" ? "hh:mm A" : "HH:mm",
              )
            : "",
          options: [],
          isConditional: baseField.is_conditional,
          isReadOnly: baseField.is_read_only,
          minValue: baseField.minValue,
          maxValue: baseField.maxValue,
          isPreventable: baseField.is_preventable,
          defaultValueType:
            // Check linked_variable in config first (for identification fields)
            (baseField.config?.linked_variable &&
              typeof baseField.config.linked_variable === "object" &&
              baseField.config.linked_variable.type === "variable") ||
            (baseField.default_value &&
              typeof baseField.default_value === "object" &&
              baseField.default_value?.type === "variable")
              ? "var"
              : (baseField.config?.linked_variable &&
                    typeof baseField.config.linked_variable === "object" &&
                    baseField.config.linked_variable.type ===
                      "interoperability") ||
                  (baseField.default_value &&
                    typeof baseField.default_value === "object" &&
                    baseField.default_value?.type === "interoperability")
                ? "cam"
                : "inp",
          defaultValueVariable: deformatDefaultValue(baseField) as Variable,
        };

        switch (field.type) {
          case "text":
            field.maxLength = baseField.config.max_length || undefined;
            field.mask = baseField.config.mask || undefined;
            break;
          case "textarea":
            field.maxLength = baseField.config.max_length || undefined;
            break;
          case "email":
            field.allowedDomains =
              baseField.config.allowed_domains || undefined;
            field.disallowedDomains =
              baseField.config.blocked_domains || undefined;
            field.maxLength = baseField.config.max_length || undefined;
            break;
          case "number":
            field.maxLength =
              baseField.config.max?.toString().length || undefined;
            // field.allow_decimals = baseField.config.allow_decimals;
            field.maxValue = baseField.config.max;
            field.minValue = baseField.config.min;
            field.precision = decimalsDeformat(
              baseField.config.precision!,
              baseField.config.allow_decimals,
            );
            field.numberFormat = baseField.config.number_format ?? "normal";
            break;
          case "select":
            field.options = baseField.config.options
              ? baseField.config.options.map((opt) => opt.label)
              : [];
            field.is_var_by_registry = baseField.config.is_var_by_registry;
            field.multiple = baseField.config.multiple;
            field.valueSource = baseField.config.options_type;
            field.variableRef = {
              type: baseField.config.data_center_value_type!,
              groupId: baseField.config.data_center_node_id!,
              groupIcon: {
                className: "text-info-quiet",
                name: "table",
                size: "xs",
                type: "far",
              },
              groupLabels: [
                baseField.config.data_center_node!,
                baseField.config.data_center_table!,
              ],
              itemIcon: {
                name: "columns",
                type: "far",
              },
              itemLabel: baseField.config.data_center_column!,
              itemValue: baseField.config.data_center_column!,
            };
            break;
          case "datetime":
          case "date":
          case "date_range":
          case "datetime_range": {
            const rawMin =
              baseField.config.min_datetime || baseField.config.min_date;
            const rawMax =
              baseField.config.max_datetime || baseField.config.max_date;
            const isNowMin =
              typeof rawMin === "string" &&
              rawMin.trim().toLowerCase().replace(/[()]/g, "") === "now";
            const isNowMax =
              typeof rawMax === "string" &&
              rawMax.trim().toLowerCase().replace(/[()]/g, "") === "now";
            field.minDate = isNowMin ? "now" : normalizeDate(rawMin);
            field.maxDate = isNowMax ? "now" : normalizeDate(rawMax);
            field.includeTime = baseField.config.includeTime;
            field.format24Hour = baseField.config.time_format === "24h";
            break;
          }
          case "file":
            field.fileTypes = baseField.config.allowed_mimes || [];
            field.multipleFiles = baseField.config.multiple || false;
            field.allowAnyFile =
              baseField.config.allowed_mimes?.includes("*") || false;
            field.isNitHomologado = baseField?.config?.isNitHomologado;
            field.maxFileSize =
              baseField.config.max_file_size_mb ||
              baseField.config.max_total_size_mb;
            field.fileSizeKey = baseField.config.max_file_size_mb
              ? false
              : true;
            break;
          case "phone":
            field.allowCountry = baseField.config.allow_extensions || false;
            break;
          case "identification":
            field.allowUserChooseDocType =
              baseField.config.allow_select_document_type || false;
            field.documentType = baseField.config.document_type;
            break;

          case "interoperability":
            field.service = baseField.config.service;
            field.action = baseField.config.action;
            field.input_field = baseField.config.input_field?.map((input) => {
              if (
                input.placeholder &&
                /^\d{4}-\d{2}-\d{2}$/.test(input.placeholder)
              ) {
                const [year, month, day] = input.placeholder.split("-");
                return {
                  ...input,
                  placeholder: `${day}/${month}/${year}`,
                };
              }

              return input;
            });
            field.output_values = baseField.config.output_values;
            break;

          case "switch":
            field.label = baseField.config.label;
            break;
        }

        // Condiciones
        if (baseField.conditions && baseField.conditions.length > 0) {
          field.isConditional = true;
          field.conditionsOperator = (baseField.conditions[0]?.operator ||
            "OR") as "OR" | "AND";
          field.conditions = baseField.conditions.map((c, condIndex) => {
            // refLabel es el nombre visible del campo (label)
            const refLabel =
              (c.field_reference && nameToLabel[c.field_reference]) ||
              c.field_reference;
            console.log(refLabel);
            // field_reference es el technicalKey único del campo
            const fieldRef = c.field_reference || "";
            return {
              id: `cond-${fieldIndex + 1}-${condIndex + 1}`,
              data: {
                selectedValue: {
                  // id debe ser el technicalKey para coincidir con extractFields
                  id: fieldRef,
                  // name es el label visible para mostrar en la UI
                  name: refLabel,
                  type: c.field_reference_type,
                },
                selectedOperation: {
                  name: c.condition_type || "",
                  label: mapConditionTypeBackName(c.condition_type || ""),
                  needsComparativeValue: !!c.value,
                  comparativeValueType: c.field_reference_type,
                },
                compareValueType: typeof c.value,
                compareInput:
                  c.value instanceof Array
                    ? arrayToString(c.value)
                    : String(c.value ?? ""),
                compareDate:
                  c.value instanceof Date ? c.value : (c.value as string) || "",
                selectedVar: {
                  id: fieldRef,
                  name: fieldRef,
                  type: "text",
                },
              },
            };
          });
        }

        return field;
      }),
    })),
  }));
};

// Inverso de mapFieldType
const mapFieldTypeBack = (type?: string): FormField["type"] => {
  switch (type) {
    case "dui_nit":
      return "identification";
    case "switch":
      return "switch";
    default:
      return (type as FormField["type"]) || "text";
  }
};

const mapConditionTypeBackName = (conditionType: string): string => {
  switch (conditionType) {
    case "IS_EMPTY":
      return "Está vacío";
    case "IS_NOT_EMPTY":
      return "No está vacío";
    case "EQUALS":
      return "Es igual a";
    case "NOT_EQUALS":
      return "No es igual a";
    case "STARTS_WITH":
      return "Empieza con";
    case "ENDS_WITH":
      return "Termina con";
    case "GREATER_THAN":
      return "Es mayor a";
    case "LESS_THAN":
      return "Es menor a";
    case "GREATER_THAN_OR_EQUAL":
      return "Es mayor o igual a";
    case "LESS_THAN_OR_EQUAL":
      return "Es menor o igual a";
    case "EXISTS":
      return "Existe";
    case "NOT_EXISTS":
      return "No existe";
    case "CONTAINS":
      return "Contiene";
    case "NOT_CONTAINS":
      return "No contiene";
    case "IN_LIST":
      return "Está en la lista";
    case "NOT_IN_LIST":
      return "No está en la lista";

    case "IS_TRUE":
      return "Es verdadero";
    case "IS_FALSE":
      return "Es falso";

    case "IS_AFTER":
      return "Es después de";
    case "IS_BEFORE":
      return "Es antes de";
    case "IS_ON_OR_AFTER":
      return "Es después o igual a";
    case "IS_ON_OR_BEFORE":
      return "Es antes o igual a";

    default:
      return "Desconocido";
  }
};

const buildVariableDefaultValue = (variable: {
  id: string;
  variable: string;
  node_id: string;
  output_name: string;
  field_id: string;
}): Variable => {
  if (variable.output_name) {
    return {
      id: `${variable?.node_id}-${variable?.output_name}`,
      name: variable?.variable,
      node_id: variable?.node_id,
      output_name: variable?.output_name,
      field_id: variable?.field_id,
      type: "string",
    };
  }
  return {
    id: variable?.node_id,
    name: variable?.variable,
    node_id: variable?.node_id,
    output_name: variable?.output_name,
    field_id: variable?.field_id,
    type: "string",
  };
};

/**
 * Applies dynamic date constraints to form steps at render time.
 * When a date config value is "now", it is replaced with today's date.
 *
 * This ensures that date constraints like "no earlier than today" remain dynamic
 * instead of being hardcoded to a specific date at configuration time.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyDynamicDates<T extends any[]>(steps: T): T {
  const today = dayjs().format("DD/MM/YYYY");
  const todayWithTime = dayjs().format("DD/MM/YYYY HH:mm");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return steps.map((step: any) => ({
    ...step,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sections: step.sections?.map((section: any) => ({
      ...section,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fields: section.fields?.map((field: any) => {
        const config = field.config;
        if (!config) return field;

        const isDateType = [
          "date",
          "datetime",
          "date_range",
          "datetime_range",
        ].includes(field.type);
        if (!isDateType) return field;

        const newConfig = { ...config };

        if (newConfig.min_date === "now") {
          newConfig.min_date = today;
        }
        if (newConfig.max_date === "now") {
          newConfig.max_date = today;
        }
        if (newConfig.min_datetime === "now") {
          newConfig.min_datetime = todayWithTime;
        }
        if (newConfig.max_datetime === "now") {
          newConfig.max_datetime = todayWithTime;
        }

        return { ...field, config: newConfig };
      }),
    })),
  })) as T;
}
